import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react'
import { useNotifications } from './NotificationsContext'

const WebSocketContext = createContext()

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  const { addNotification } = useNotifications()
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const MAX_RECONNECT_ATTEMPTS = 5
  const RECONNECT_DELAY = 3000 // 3 seconds

  // Initialize WebSocket connection
  const connectWebSocket = useCallback(() => {
    // Replace with your Node-RED WebSocket URL
    const wsUrl = process.env.REACT_APP_NODE_RED_WS_URL

    const ws = new WebSocket(`${wsUrl}/sync`)
    wsRef.current = ws

    ws.onopen = () => {
      setIsConnected(true)
      reconnectAttemptsRef.current = 0
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)

        handleWebSocketMessage(message)
      } catch (error) {
        console.error('Error processing WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      setIsConnected(false)
    }

    ws.onclose = (event) => {
      setIsConnected(false)

      // Attempt to reconnect if not at max attempts
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1
          connectWebSocket()
        }, RECONNECT_DELAY)
      } else {
      }
    }
    // eslint-disable-next-line
  }, [])

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback(
    (message) => {
      const { type, action, data } = message

      // Add notification for the user
      addNotification({
        title: `${type} ${action}`,
        description: `A ${type.toLowerCase()} has been ${action.toLowerCase()}ed`,
        type: 'info'
      })

      // Dispatch a custom event that components can listen for
      const event = new CustomEvent('websocket-message', {
        detail: { type, action, data }
      })

      window.dispatchEvent(event)
    },
    [addNotification]
  )

  // Send a message through the WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
      return true
    } else {
      console.warn('WebSocket is not open, cannot send message')
      return false
    }
  }, [])

  // Connect WebSocket on mount
  useEffect(() => {
    connectWebSocket()

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [connectWebSocket])

  const value = {
    isConnected,
    sendMessage
  }

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
